import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import SettingsModal from './SettingsModal';
import HighscoreModal from './HighscoreModal';
import StatsModal from './StatsModal';
import LevelsModal from './LevelsModal';

function MainMenu({ onPlay, musicSettings, setMusicSettings }) {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const bgOffsetRef = useRef(0);
  const [isMenuActive, setIsMenuActive] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isHighscoreOpen, setIsHighscoreOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isLevelsOpen, setIsLevelsOpen] = useState(false);
  const [isPrimaryPortrait, setIsPrimaryPortrait] = useState(
    isMobile && (window.orientation === 0 || window.screen.orientation?.angle === 0)
  );
  const blocksRef = useRef([]);

  // Fallback for musicSettings
  const safeMusicSettings = musicSettings || {
    musicVolume: 0.5,
    sfxVolume: 0.5,
    isMusicEnabled: true,
    isSfxEnabled: true,
  };

  // Log warning if musicSettings is undefined
  useEffect(() => {
    if (!musicSettings) {
      console.warn('musicSettings is undefined in MainMenu, using fallback');
    }
    if (!setMusicSettings) {
      console.warn('setMusicSettings is undefined in MainMenu');
    }
  }, [musicSettings, setMusicSettings]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const gridSize = 64; // Match Game component's grid size
    const scrollSpeed = 0.1; // Pixels per frame, adjusted for smoother flow

    // Load images
    const images = {
      bg: new Image(),
      blueBlock: new Image(),
      spike: new Image(),
    };
    images.bg.src = '/bg2.jpg';
    images.blueBlock.src = '/blueBlock.png';
    images.spike.src = '/spike.png';

    let isImagesReady = false;
    let failedImages = [];

    const loadImages = () => {
      Promise.all(
        Object.values(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalWidth !== 0) {
                resolve();
              } else {
                img.onload = () => resolve();
                img.onerror = () => {
                  failedImages = [...failedImages, img.src];
                  resolve();
                };
              }
            })
        )
      ).then(() => {
        isImagesReady = true;
      });
    };

    loadImages();

    // Resize canvas to match window size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // Initialize blocks to cover canvas and extend beyond right edge
      blocksRef.current = generateInitialBlocks(canvas.width, gridSize);
    };

    // Generate initial blocks to cover canvas plus buffer
    const generateInitialBlocks = (canvasWidth, gridSize) => {
      const blockCount = Math.ceil(canvasWidth / gridSize) + 2; // Cover canvas + buffer
      return Array.from({ length: blockCount }, (_, i) => ({
        x: i * gridSize, // Position in pixels
        y: 0, // At bottom (adjusted in draw)
        type: Math.random() < 0.2 ? 'spike' : 'blueBlock',
      }));
    };

    // Update blocks for continuous flow
    const updateBlocks = (canvasWidth, gridSize) => {
      // Move blocks left
      blocksRef.current.forEach((block) => {
        block.x -= scrollSpeed;
      });

      // Remove blocks that are off-screen to the left
      blocksRef.current = blocksRef.current.filter((block) => block.x > -gridSize);

      // Add new blocks on the right if needed
      const maxX = blocksRef.current.length > 0 ? Math.max(...blocksRef.current.map((b) => b.x)) : 0;
      if (maxX < canvasWidth + gridSize) {
        blocksRef.current.push({
          x: maxX + gridSize, // Place just after the last block
          y: 0,
          type: Math.random() < 0.2 ? 'spike' : 'blueBlock',
        });
      }
    };

    const handleOrientationChange = () => {
      setIsPrimaryPortrait(
        isMobile && (window.orientation === 0 || window.screen.orientation?.angle === 0)
      );
      resizeCanvas();
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('orientationchange', handleOrientationChange);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!isImagesReady) {
        ctx.fillStyle = 'gray';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        return;
      }

      // Draw scrolling background
      if (images.bg.complete && images.bg.naturalWidth !== 0) {
        const bgWidth = canvas.width;
        const overlap = 6;
        let offset = (bgOffsetRef.current * bgWidth) % bgWidth;
        if (offset > 0) offset -= bgWidth;
        const bgX1 = offset;
        const bgX2 = offset + bgWidth - overlap;

        ctx.drawImage(images.bg, bgX1, 0, bgWidth, canvas.height);
        ctx.drawImage(images.bg, bgX2, 0, bgWidth, canvas.height);
      } else {
        ctx.fillStyle = 'gray';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Draw blocks and spikes
      ctx.globalAlpha = 0.8; // Set opacity to 50% for blocks and spikes
      blocksRef.current.forEach((block) => {
        const img = images[block.type];
        const x = block.x;
        const y = canvas.height - gridSize; // Place at bottom
        if (
          img &&
          img.complete &&
          img.naturalWidth !== 0 &&
          x > -gridSize &&
          x < canvas.width + gridSize
        ) {
          ctx.drawImage(img, x, y, gridSize, gridSize);
        }
      });
      ctx.globalAlpha = 1.0; // Restore opacity for other drawings

      // Display failed images if any
      if (failedImages.length > 0) {
        ctx.fillStyle = 'red';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('Failed to load:', 20, 60);
        failedImages.forEach((img, index) => {
          ctx.fillText(img.split('/').pop(), 20, 100 + index * 40);
        });
      }
    };

    const animate = (time) => {
      if (!isMenuActive) return;

      // Update background offset
      bgOffsetRef.current += scrollSpeed / canvas.width;
      if (bgOffsetRef.current >= 1) {
        bgOffsetRef.current -= 1;
      }

      // Update blocks
      updateBlocks(canvas.width, gridSize);

      draw();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    if (isMenuActive) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMenuActive]);

  const handlePlay = () => {
    setIsLevelsOpen(true);
  };

  const handleLevelSelect = (level) => {
    if (!Number.isInteger(level) || level < 1) {
      console.warn(`Invalid level selected: ${level}. Defaulting to level 1.`);
      level = 1;
    }
    setIsMenuActive(false);
    setIsLevelsOpen(false);
    onPlay(safeMusicSettings, level);
  };

  const handleSettingsToggle = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  const handleHighscoreToggle = () => {
    setIsHighscoreOpen(!isHighscoreOpen);
  };

  const handleStatsToggle = () => {
    setIsStatsOpen(!isStatsOpen);
  };

  const handleLevelsClose = () => {
    setIsLevelsOpen(false);
  };

  return (
    <div className="relative flex flex-col items-center justify-center min-h-[100dvh] w-[100dvw] overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
      />
      <div className="absolute top-4 right-4 flex flex-col space-y-2 z-20">
        <button
          onClick={handleHighscoreToggle}
          className={`relative transition-all duration-300 hover:scale-105 hover:filter hover:drop-shadow-[0_0_12px_rgba(0,255,255,0.6)] animate-wobble ${
            isPrimaryPortrait ? 'brightness-50 pointer-events-none' : ''
          }`}
        >
          <img
            src="/highscoreUi.png"
            alt="Highscore Button"
            className="w-12 sm:w-14 md:w-16 lg:w-18 landscape:w-10 object-contain"
          />
        </button>
        <button
          onClick={handleStatsToggle}
          className={`relative transition-all duration-300 hover:scale-105 hover:filter hover:drop-shadow-[0_0_12px_rgba(0,255,255,0.6)] animate-wobble ${
            isPrimaryPortrait ? 'brightness-50 pointer-events-none' : ''
          }`}
        >
          <img
            src="/statUi.png"
            alt="Stats Button"
            className="w-12 sm:w-14 md:w-16 lg:w-18 landscape:w-10 object-contain"
          />
        </button>
      </div>
      {isPrimaryPortrait && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-10 pointer-events-auto">
          <img
            src="/tiltPhone.gif"
            alt="Tilt Phone to Landscape"
            className="max-w-[80%] max-h-[80%] object-contain"
          />
        </div>
      )}
      <div className="flex flex-row items-center justify-center w-full max-w-[600px] px-4 space-x-4 sm:space-x-6 landscape:flex-row landscape:items-center landscape:space-x-6">
        <button
          onClick={handlePlay}
          className={`relative transition-all duration-300 hover:scale-105 hover:filter hover:drop-shadow-[0_0_12px_rgba(0,255,255,0.6)] landscape:order-1 ${
            isPrimaryPortrait ? 'brightness-50 pointer-events-none' : ''
          }`}
        >
          <img
            src="/playUi.png"
            alt="Play Button"
            className="w-20 sm:w-24 md:w-28 lg:w-32 landscape:w-16 object-contain"
          />
        </button>
        <img
          src="/title.png"
          alt="Dashcore Title"
          className={`w-3/4 max-w-[240px] sm:max-w-[280px] md:max-w-[320px] lg:max-w-[360px] landscape:max-w-[200px] object-contain filter drop-shadow-[0_0_10px_rgba(0,255,255,0.4)] landscape:order-2 ${
            isPrimaryPortrait ? 'brightness-50' : ''
          }`}
        />
        <button
          onClick={handleSettingsToggle}
          className={`relative transition-all duration-300 hover:scale-105 hover:filter hover:drop-shadow-[0_0_12px_rgba(0,255,255,0.6)] landscape:order-3 ${
            isPrimaryPortrait ? 'brightness-50 pointer-events-none' : ''
          }`}
        >
          <img
            src="/settingsUi.png"
            alt="Settings Button"
            className="w-20 sm:w-24 md:w-28 lg:w-32 landscape:w-16 object-contain"
          />
        </button>
      </div>
      {isSettingsOpen && (
        <SettingsModal
          musicSettings={safeMusicSettings}
          setMusicSettings={setMusicSettings}
          onClose={handleSettingsToggle}
        />
      )}
      {isHighscoreOpen && <HighscoreModal onClose={handleHighscoreToggle} />}
      {isStatsOpen && <StatsModal onClose={handleStatsToggle} />}
      {isLevelsOpen && (
        <LevelsModal onSelectLevel={handleLevelSelect} onClose={handleLevelsClose} />
      )}
    </div>
  );
}

export default MainMenu;